import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Loading from '../../components/Loading/Loading';
import {useAccountContext} from '../../contexts/AccountContext';
import {useReviewContext} from '../../contexts/ReviewContext';
import {useContactVulnerabilities} from '../../features/vulnerabilities/useContactVulnerabilities';
import {useVulnerabilityOptions} from '../../features/vulnerabilities/useVulnerabilityOptions';
import {themeColors} from '../../theme/Theme';
import {SummarySection} from '../HandoverSummaryPage/SummarySection';
import {PostcursorSummary} from '../PostcursorPage/PostcursorSummary';
import {SectionContact} from './ContactVulnerabilitySection';

const SPP_PROPOSITION_TYPE = 'SPP';
const SECTION_COLOURS = [themeColors.coral, themeColors.green, themeColors.yellow];

const ContactVulnerabilitySummaryTable: React.FC<{
  contact: SectionContact;
  reviewId: string;
}> = ({contact, reviewId}) => {
  const {existing, drafts, loading} = useContactVulnerabilities(contact.id, {reviewId});
  const activeRows = existing.filter((row) => !row.endDate);

  if (loading) {
    return <Loading entityName="vulnerabilities" />;
  }
  if (activeRows.length === 0 && drafts.length === 0) {
    return <Typography color="text.secondary">No vulnerabilities recorded</Typography>;
  }
  return (
    <Table
      size="small"
      aria-label={`${contact.simplifiedName || 'Contact'} vulnerabilities`}
    >
      <TableHead>
        <TableRow>
          <TableCell>Vulnerability</TableCell>
          <TableCell>Service Level</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {activeRows.map((row) => (
          <TableRow key={row.id}>
            <TableCell>{row.vulnerabilityType}</TableCell>
            <TableCell>{row.serviceLevel}</TableCell>
            <TableCell>
              {row.pendingEndReview?.id === reviewId
                ? 'Ending with this review'
                : 'Continuing'}
            </TableCell>
          </TableRow>
        ))}
        {drafts.map((row) => (
          <TableRow key={row.id}>
            <TableCell>{row.vulnerabilityType || 'Not chosen'}</TableCell>
            <TableCell>{row.serviceLevel || 'Not chosen'}</TableCell>
            <TableCell>
              {row.isComplete ? 'New with this review' : 'Incomplete'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/**
 * Read-only view of the SFP Vulnerability tube stop for the handover summary
 * page: per contact, what continues, what ends and what is new in this review.
 */
export const VulnerabilitySummary: React.FC = () => {
  const {account} = useAccountContext();
  const {reviewDetails} = useReviewContext();
  const options = useVulnerabilityOptions();

  if (!account || !reviewDetails || options.loading || !options.isEnabled) {
    return null;
  }
  const contacts: SectionContact[] = [
    account.primaryContact,
    account.secondaryContact,
  ].flatMap((contact) =>
    contact ? [{id: contact.id, simplifiedName: contact.simplifiedName}] : []
  );
  const editable = reviewDetails.isDraft || false;

  return (
    <>
      {contacts.map((contact, index) => {
        const name = contact.simplifiedName || 'Contact';
        return (
          <SummarySection
            key={contact.id}
            step="resilience"
            editable={editable}
            color={SECTION_COLOURS[index % SECTION_COLOURS.length]}
            title={`${name}'s vulnerabilities`}
            test={`${name} vulnerability`}
          >
            <ContactVulnerabilitySummaryTable
              contact={contact}
              reviewId={reviewDetails.id}
            />
          </SummarySection>
        );
      })}
    </>
  );
};

/**
 * The resilience part of the handover summary. Mirrors `ResilienceStepPage`:
 * SFP household accounts see the vulnerability tables, SPP and trust accounts
 * keep the legacy postcursor summary.
 */
export const ResilienceSummary: React.FC = () => {
  const {account} = useAccountContext();
  if (!account) {
    return null;
  }
  if (account.propositionType === SPP_PROPOSITION_TYPE || account.isTrust) {
    return <PostcursorSummary showFooter={false} />;
  }
  return <VulnerabilitySummary />;
};
